<script lang="ts">
import type { InputDateProps } from '@nuxt/ui';
import type { PrimitiveType } from '#layers/data-model/public';
import { PRIMITIVE_TYPE_DATE } from '#layers/data-model/public';

export const supportedDateFieldTypes = ({ primitiveType }: { primitiveType: PrimitiveType }) =>
	primitiveType.name === PRIMITIVE_TYPE_DATE ? ['datetime'] : [];

const __DsBlock = {
	icon: 'lucide:calendar',
	name: '$t:blocks.input_date.name',
	description: '$t:blocks.input_date.description',
	type: 'input',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: supportedDateFieldTypes,
		recommendedDisplays: ['$builtin:DisplayFormattedDate'],
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: BLOCK_INPUT_VARIANT_OPTIONS,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
	],
}</script>

<script lang="ts" setup>
import type { Temporal } from 'temporal-polyfill';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { calendarDateConverter, nowDate } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS, BLOCK_INPUT_VARIANT_OPTIONS } from '#layers/blocks/constants/ui-options';
import { usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice } from '#layers/ui/public';

interface Props extends InputBlockProps, Pick<InputDateProps, 'variant' | 'size' | 'disabled'> {
	block?: boolean;
	// Absorbs the shared placeholder ("NULL") DefaultValueInput passes to every input block; date inputs don't render it.
	placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
	block: true,
});

const model = defineModel<Temporal.PlainDate | null>();

;

const forwarded = reactivePick(props, 'variant', 'size', 'disabled', 'block', 'readonly');

const { modelValue: m, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });

const calendarModel = computed({
	get: () => calendarDateConverter.toCalendar(m.value),
	set: (value) => {
		m.value = calendarDateConverter.toTemporal(value);
	},
});

const setToday = () => {
	m.value = nowDate();
};
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DInputDate
			v-bind="forwarded"
			v-model="calendarModel"
			class="w-full min-w-0 gap-0.5 overflow-hidden"
			:aria-readonly="readonly"
		>
			<template #trailing>
				<DPopover
					:content="{ side: 'bottom', align: 'end' }"
					:ui="{
						content: 'p-2 text-center',
					}"
				>
					<DButton
						v-if="!readonly"
						color="neutral"
						variant="link"
						data-testid="input-date-calendar"
						icon="lucide:calendar"
						:disabled
					/>

					<template #content>
						<DCalendar v-model="calendarModel" />
						<DSeparator class="my-2" />
						<DButton
							v-if="!readonly"
							variant="link"
							data-testid="input-date-now"
							@click="setToday"
						>
							{{ $t('blocks.input_date.now') }}
						</DButton>
					</template>
				</DPopover>
			</template>
		</DInputDate>
	</DTooltip>
</template>
